import {Program} from "@/program/interfaces/Program";
import {ProgramBookable} from "@/program/interfaces/ProgramBookable";
import {HealthJourneyRecapStatus} from "@/core/http/resources/AppointmentResource";
import {ActNature, HealthJourneyActRequest} from "@/agenda/interfaces/healthJourney";

/**
 * Acts a single journey may hold, mirrored from the server's own bound.
 *
 * Held here so the screen can say which act is the one too many, rather than
 * letting a whole series be refused with nothing to act on.
 */
export const MAX_ACTS = 20;

/**
 * One act of the journey being edited.
 *
 * The booker travels with it because the appointments are written from it:
 * their title, their description and their duration all come from the
 * catalogue entry the act was built out of.
 */
export interface JourneyAct {
    clientRef: string;
    /** The support program, as both the planner and the appointment name it. */
    programId: number | null;
    programName: string | null;
    bookerId: number | null;
    booker: ProgramBookable | null;
    appointmentTypeId: number;
    label: string;
}

/**
 * The counter's own name for an act.
 *
 * Built from catalogue identifiers, which say nothing about the patient, and
 * stable across a recalculation: removing an act above this one moves its
 * rank but not its reference, so a pin the pharmacist set stays on the act
 * they set it on.
 */
export const actRef = (programId: number | null, bookerId: number | null): string => {
    return `p${programId ?? 0}b${bookerId ?? 0}`;
};

/** The acts of a program, in the order its catalogue holds them. */
export const bookersOf = (program: Program): ProgramBookable[] => {
    return [...(program.bookers ?? [])].sort((first, second) => {
        return (first.position ?? 0) - (second.position ?? 0) || first.id - second.id;
    });
};

/**
 * The acts a program contributes to a journey.
 *
 * A booker naming no appointment type is left out: the planner is asked for a
 * type, and nothing here could invent one. The caller counts what was dropped
 * so the omission is said rather than silently shortening the series.
 */
export const actsOfProgram = (program: Program): JourneyAct[] => {
    return bookersOf(program)
        .filter((booker) => booker.appointment_type_id !== null)
        .map((booker) => ({
            clientRef: actRef(program.programmable_id, booker.id),
            programId: program.programmable_id,
            programName: program.name,
            bookerId: booker.id,
            booker,
            appointmentTypeId: booker.appointment_type_id as number,
            label: booker.appointment_title || booker.title || program.name
        }));
};

/** Acts of a program the catalogue cannot hand to the planner. */
export const untypedActsOf = (program: Program): number => {
    return bookersOf(program).filter((booker) => booker.appointment_type_id === null).length;
};

/**
 * The acts as the planner takes them.
 *
 * The regulatory delay is left out on purpose: the server reads it from the
 * catalogue and overrides what it is sent, so announcing a figure could only
 * produce a disagreement over a screen left open since yesterday.
 */
export const toActRequests = (acts: JourneyAct[]): HealthJourneyActRequest[] => {
    return acts.map((act) => ({
        client_ref: act.clientRef,
        appointment_type_id: act.appointmentTypeId,
        program_id: act.programId,
        booker_id: act.bookerId,
        duration_minutes: act.booker?.appointment_duration || null
    }));
};

/** What kind of act it is, in the pharmacist's terms. */
export const NATURE_LABELS: Record<ActNature, string> = {
    vaccine: 'Vaccination',
    interview: 'Entretien',
    other: 'Acte'
};

/**
 * Why an act was put where it was.
 *
 * A code this build does not know renders nothing rather than its raw slug:
 * the server's list grows, and the worded sentence and the figures beside it
 * already carry the substance.
 */
export const REASON_LABELS: Record<string, string> = {
    delai_reglementaire_applique: 'Délai réglementaire appliqué',
    espacement_preference_applique: 'Espacement souhaité par l’officine',
    etape_figee: 'Moment que vous avez choisi',
    etape_figee_en_conflit: 'Moment choisi malgré les règles',
    meme_collaborateur: 'Même collaborateur',
    collaborateur_non_habilite: 'Collaborateur non habilité',
    collaborateur_sans_planning: 'Collaborateur sans planning',
    unconfigured_team: 'Au nom de l’officine, équipe non paramétrée',
    hors_horizon_agenda: 'Au-delà de l’agenda lisible',
    aucun_creneau_disponible: 'Aucun créneau disponible',
    acte_indisponible: 'Acte non proposé par l’officine',
    duree_inconnue: 'Durée inconnue',
    seul_creneau_disponible: 'Seul créneau disponible',
    affluence_faible: 'Affluence faible',
    effectif_confortable: 'Effectif confortable',
    creneau_disponible: 'Créneau disponible'
};

/** Why days were ruled out while searching for a moment. */
export const EXCLUSION_LABELS: Record<string, string> = {
    contrainte_jour: 'écarté par une contrainte',
    plafond_atteint: 'plafond quotidien atteint',
    officine_fermee: 'officine fermée',
    aucun_collaborateur_disponible: 'aucun collaborateur disponible'
};

/** Why two acts were not held on the same visit. */
export const COMBINATION_LABELS: Record<string, string> = {
    delai_reglementaire: 'un délai réglementaire les sépare',
    acte_non_placable: 'l’un des deux n’a pas pu être placé',
    etape_figee: 'l’un des deux est figé sur un autre moment',
    preference_desactivee: 'le cumul de ces natures est désactivé dans vos règles',
    duree_maximale: 'la visite dépasserait la durée maximale',
    aucun_collaborateur_double_habilitation: 'aucun collaborateur n’est habilité pour les deux'
};

/** What became of an act. */
export const STATUS_LABELS: Record<string, string> = {
    placed: 'Programmé',
    estimated: 'Date estimée',
    unplaced: 'Non placé'
};

/**
 * What to tell the pharmacist when the recap did not reach the patient whole.
 *
 * Nothing is said when it did: the appointments are what matters, and a toast
 * per outcome would make the ordinary case as loud as the exception. Note that
 * one of these is not a failure to send — see `no_link`.
 */
const RECAP_WARNINGS: Record<HealthJourneyRecapStatus, string | null> = {
    sent: null,
    no_phone: 'Les rendez-vous sont programmés, mais le patient n’a pas de mobile : le récapitulatif n’a pas pu être envoyé.',
    no_credit: 'Les rendez-vous sont programmés, mais votre solde de SMS est insuffisant : le récapitulatif n’a pas été envoyé.',
    // The message did leave. What it could not carry is the link to the
    // patient's own appointments page, so saying it was not sent would send
    // the pharmacist chasing a patient who has already been told.
    no_link: 'Les rendez-vous sont programmés et le patient a reçu son récapitulatif, mais sans le lien vers ses rendez-vous.',
    no_consent: 'Les rendez-vous sont programmés. Le patient ne souhaite pas être notifié de ses rendez-vous : aucun récapitulatif ne lui a été envoyé.'
};

/**
 * Whether a patient was texted, and what to say when they were not.
 *
 * Anything other than `sent` warns, including an outcome this build does not
 * know: the health server may name a new failure after this version ships,
 * and a table lookup that silently missed would report a patient who was
 * never reached as a plain success — which is the one thing the counter has
 * to know before they leave.
 */
export const recapWarning = (recap: HealthJourneyRecapStatus | null): string | null => {
    if (recap === 'sent') {
        return null;
    }

    if (recap === null) {
        return 'Les rendez-vous sont programmés, mais l’envoi du récapitulatif n’a pas pu être confirmé.';
    }

    return RECAP_WARNINGS[recap]
        ?? 'Les rendez-vous sont programmés, mais le récapitulatif n’a pas pu être envoyé au patient.';
};
